import React, { useEffect, useRef } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { formatDate } from '../../utils/dateFormatter';
import { TEXT, DETAIL_ROWS, errorText } from '../../config/listingPerformanceLabels';
import { usePerformanceUnit } from '../../hooks/useListingPerformance';
import { Figure, TrendArrow, VerdictChip, BadgeLostTag, reasonText, averageKind, ReadingNotes } from './parts';
import { OccupancyChart, ReviewStarsChart } from './WeeklyCharts';
import ChangeLog from './ChangeLog';
import WeekReading from './WeekReading';
import { MarketRows } from './MarketPrice';

/**
 * «أداء الوحدات» — one unit, read top-down the way ops dig in (15 Oct review):
 *   1. the answer: the verdict, WHY (the rule that fired, with this unit's numbers) and the experiment
 *      running that week;
 *   2. the shape: the two 8-week charts side by side, a mark under every week something was changed;
 *   3. the evidence: this week · prior week · 4-week average · trend, grouped as occupancy, the Airbnb
 *      funnel, rating and price — the last group ending with «سعر السوق» and «مقابل السوق» (staff, while the
 *      market price comparison is on);
 *   4. a unit that needs review: «قراءة الأسبوع» — the AI's suggestion + the collection notes in ONE card
 *      (any other unit: the notes as before);
 *   5. the history: the change log, folded, latest first, one line each; each change carries the counted box, and
 *      a change waiting for its result opens the log by itself.
 * Every number is the server's; the owner view's answer carries no verdict, notes or hypotheses.
 */

const GROUPS = [
  { key: 'calendar', rows: ['occupancy', 'openNights'] },
  { key: 'funnel', rows: ['impressions', 'pageViews', 'similarPageViews', 'entryRate', 'overallBookingRate', 'similarBookingRate', 'newBookings'] },
  { key: 'quality', rows: ['rating', 'reviewCount', 'badges', 'guestPrice', 'bookedNightRate'] }
];

const MetricRow = ({ m }) => {
  const spec = DETAIL_ROWS[m.key];
  return (
    <tr>
      <th scope="row">{spec.label}</th>
      {/* The arrow sits beside the figure it describes (this week vs the prior week), as on the board. */}
      <td data-label={TEXT.thisWeek} className="lp-detail__now">
        <span className="lp-cell"><Figure value={m.current} kind={spec.kind} /><TrendArrow trend={m.trend} /></span>
      </td>
      <td data-label={TEXT.priorWeek}><Figure value={m.prior} kind={spec.kind} /></td>
      <td data-label={TEXT.fourWeekAvg}>{spec.kind === 'text' ? '' : <Figure value={m.average} kind={averageKind(spec.kind, m.average)} />}</td>
    </tr>
  );
};

const UnitDetail = ({ unitCode, week, ownerId, view, focusDue = false, onClose }) => {
  const ownerView = view === 'owner';
  const { data, isPending, isError, error, isPlaceholderData } = usePerformanceUnit({ unitCode, ownerId, week, view });
  const ref = useRef(null);

  // Opening a row brings its detail into view — on a phone it would otherwise open off-screen. Arriving from
  // «نتائج لم تُكتب», it brings the change waiting for its result instead, which sits below the charts and the card;
  // that row exists only once the unit's answer is in. Once per opening: pressing a chip, changing tab or flipping
  // the owner view with a unit already open must never move the page.
  const landedOn = useRef(null);
  const answered = Boolean(data) || isError;
  useEffect(() => {
    if (landedOn.current === unitCode || (focusDue && !answered)) return;
    landedOn.current = unitCode;
    const dueRow = focusDue ? ref.current?.querySelector('.lp-change--due') : null;
    (dueRow || ref.current)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [unitCode, focusDue, answered]);

  const metrics = new Map((data?.metrics || []).map(m => [m.key, m]));
  // The experiment running that week, read from the unit's own log (same date, with a hypothesis).
  const running = !ownerView && data?.experimentStart
    ? (data.changes || []).find(c => c.date === data.experimentStart && c.hypothesis) || null
    : null;
  const reason = !ownerView && data?.verdict ? reasonText(data.verdict, data.verdictInputs) : '';

  return (
    <section ref={ref} className={`lp-detail${isPlaceholderData ? ' lp-stale' : ''}`} aria-labelledby="lp-detail-title">
      <div className="lp-section-head">
        <div>
          <h3 id="lp-detail-title" className="lp-detail__title">{unitCode}</h3>
          {data?.unitName && <p className="lp-detail__name">{data.unitName}</p>}
          {data?.weekEnd && (
            <p className="lp-muted">
              {TEXT.weekLabel} {formatDate(data.weekEnd)}
              {data.priorWeekEnd && <> — {TEXT.priorWeek}: {formatDate(data.priorWeekEnd, { year: undefined })}</>}
            </p>
          )}
        </div>
        <Button variant="link" className="lp-link" onClick={onClose}>
          <i className="bi bi-x-lg" aria-hidden="true" /> {TEXT.close}
        </Button>
      </div>

      {isPending && !isError && <div className="lp-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>}
      {isError && <div className="lp-error" role="alert">{errorText(error)}</div>}

      {data && !ownerView && (data.verdict || running) && (
        <div className="lp-answer">
          {data.verdict && (
            <p className="lp-answer__line">
              <VerdictChip code={data.verdict} inputs={data.verdictInputs} />
              {reason && <span className="lp-answer__why"><span className="lp-muted">{TEXT.why}:</span> {reason}</span>}
            </p>
          )}
          {data.badgeLost && (
            <p className="lp-answer__line">
              <BadgeLostTag hint={false} />
              <span>{TEXT.badgeLostLine}</span>
            </p>
          )}
          {running && (
            <p className="lp-answer__line lp-answer__experiment">
              <span className="lp-change__tag"><i className="bi bi-flask" aria-hidden="true" /> {TEXT.runningExperiment}</span>
              <span>{TEXT.experiment} {formatDate(running.date, { year: undefined })}: {running.change}</span>
              {running.reviewDate && <span className="lp-muted">— {TEXT.reviewOn} {formatDate(running.reviewDate, { year: undefined })}</span>}
            </p>
          )}
        </div>
      )}

      {data && (
        <>
          <div className="lp-detail__charts">
            <OccupancyChart points={data.charts?.occupancy || []} target={ownerView ? null : data.charts?.target} />
            <ReviewStarsChart points={data.charts?.reviews || []} />
          </div>

          {data.noPlatformNumbers && data.weekEnd && <p className="lp-banner" role="status">{TEXT.noPlatformNumbers}</p>}
          {!data.weekEnd || !(data.metrics || []).length ? (!data.weekEnd && <p className="lp-empty">{TEXT.noWeeks}</p>) : (
            <div className="lp-scroll">
              <table className="lp-table lp-detail__table">
                <thead>
                  <tr>
                    <th scope="col">{TEXT.metric}</th>
                    <th scope="col">{TEXT.thisWeek}</th>
                    <th scope="col">{TEXT.priorWeek}</th>
                    <th scope="col">{TEXT.fourWeekAvg}</th>
                  </tr>
                </thead>
                {GROUPS.map(g => {
                  const rows = g.rows.map(k => metrics.get(k)).filter(m => m && DETAIL_ROWS[m.key]);
                  const market = g.key === 'quality' && !ownerView ? data.market : null;
                  if (!rows.length && !market) return null;
                  return (
                    <tbody key={g.key} className="lp-detail__group">
                      <tr className="lp-detail__group-head"><th colSpan={4} scope="rowgroup">{TEXT.groups[g.key]}</th></tr>
                      {rows.map(m => <MetricRow key={m.key} m={m} />)}
                      {market && <MarketRows market={market} />}
                    </tbody>
                  );
                })}
              </table>
            </div>
          )}
          {!ownerView && (data.brief
            ? <WeekReading unitCode={unitCode} brief={data.brief} notes={data.notes} readOn={data.readOn} />
            : <ReadingNotes notes={data.notes} readOn={data.readOn} />)}
          {/* One log per unit and view: whether it opens is decided for this one, never carried from the last. A staff
              fold carried into the owner view left the title with no rows and no way to open them. */}
          <ChangeLog key={`${unitCode}-${view}`} unitCode={unitCode} changes={data.changes || []} ownerView={ownerView} />
        </>
      )}
    </section>
  );
};

export default UnitDetail;
